/**
 * @file 게시판 글 목록
 *
 * 시안과 같은 구성: 게시판명 + 검색 범위 드롭다운 + 검색창 + 새로고침, 그 아래 표.
 * 공지 고정 글이 항상 위(서버 정렬)에 옅은 브랜드 배경으로 붙고, 행 사이 가로선은 두지 않는다.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
    Box, IconButton, MenuItem, Pagination, Paper, Select, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import ActionButton from "../../../component/common/ActionButton";
import LoadingDots from "../../../component/common/LoadingDots";
import { boardApi } from "../api/boardApi";
import { notify } from "../../../util/toast";
import {
    BOARD_HEADER_BG, C, NOTICE_ROW_BG, ROW_HOVER_SX, SEARCH_SCOPES, formatBoardDate,
} from "../boardShared";

const PAGE_SIZE = 20;

export default function BoardPostList({ boardType, onOpenPost, onCompose, reloadKey }) {
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);
    const [scope, setScope] = useState("ALL");
    const [keywordInput, setKeywordInput] = useState("");
    /** 실제 조회에 쓰는 검색어 — 입력마다 조회하지 않고 <b>검색 버튼·Enter</b> 로만 확정한다. */
    const [keyword, setKeyword] = useState("");

    const load = useCallback(async () => {
        if (!boardType) return;
        setLoading(true);
        try {
            const data = await boardApi.listPosts(boardType.id, {
                keyword: keyword || undefined,
                scope: keyword ? scope : undefined,
                page, size: PAGE_SIZE,
            });
            setRows(Array.isArray(data?.rows) ? data.rows : []);
            setTotalPages(Math.max(1, data?.totalPages ?? 1));
            setTotalElements(data?.totalElements ?? 0);
        } catch {
            notify("글 목록을 불러오지 못했습니다", "error");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [boardType, keyword, scope, page]);

    useEffect(() => { load(); }, [load, reloadKey]);

    // 게시판을 바꾸면 페이지·검색을 초기화한다 — 3페이지에서 다른 게시판으로 넘어가면
    // 글이 3페이지도 없어 빈 목록이 뜨는 착시가 생긴다.
    useEffect(() => {
        setPage(0); setKeyword(""); setKeywordInput(""); setScope("ALL");
    }, [boardType?.id]);

    const doSearch = () => { setKeyword(keywordInput.trim()); setPage(0); };

    if (!boardType) return null;

    return (
        <Box sx={{ p: 3, maxWidth: 1300, mx: "auto" }}>
            {/* 헤더 — 게시판명 + 검색 */}
            <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "center" }}
                justifyContent="space-between" spacing={1.5} mb={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.3px", color: C.textPrimary }}>
                        {boardType.name}
                    </Typography>
                    {boardType.anonymous && (
                        <Tooltip title="익명 게시판 — 작성자가 표시되지 않습니다">
                            <VisibilityOffRoundedIcon sx={{ fontSize: 16, color: C.textMuted }} />
                        </Tooltip>
                    )}
                    {boardType.writeAdminOnly && (
                        <Tooltip title="글쓰기는 전체 관리자만 가능합니다">
                            <LockRoundedIcon sx={{ fontSize: 15, color: C.textMuted }} />
                        </Tooltip>
                    )}
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1}>
                    <Select size="small" value={scope} onChange={e => setScope(e.target.value)}
                        sx={{ fontSize: 13, minWidth: 92, bgcolor: C.surface }}>
                        {SEARCH_SCOPES.map(s => (
                            <MenuItem key={s.value} value={s.value} sx={{ fontSize: 13 }}>{s.label}</MenuItem>
                        ))}
                    </Select>
                    <TextField
                        size="small" placeholder="검색"
                        value={keywordInput}
                        onChange={e => setKeywordInput(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") doSearch(); }}
                        sx={{ width: { xs: "100%", sm: 240 }, "& .MuiOutlinedInput-root": { fontSize: 13, bgcolor: C.surface } }}
                        InputProps={{
                            endAdornment: (
                                <IconButton size="small" onClick={doSearch} sx={{ p: 0.3 }}>
                                    <SearchRoundedIcon sx={{ fontSize: 18, color: C.textMuted }} />
                                </IconButton>
                            ),
                        }}
                    />
                    <Tooltip title="새로고침">
                        <IconButton size="small" onClick={load}
                            sx={{ border: `1px solid ${C.border}`, bgcolor: C.surface }}>
                            <RefreshRoundedIcon sx={{ fontSize: 17, color: C.textSecondary }} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            {boardType.description && (
                <Typography sx={{ fontSize: 12.5, color: C.textMuted, mb: 2 }}>{boardType.description}</Typography>
            )}

            {/* 건수 + 글쓰기 */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.25}>
                <Typography sx={{ fontSize: 12.5, color: C.textSecondary }}>
                    전체 <Box component="span" sx={{ fontWeight: 700, color: C.textPrimary }}>{totalElements}</Box>건
                    {keyword && ` · '${keyword}' 검색 결과`}
                </Typography>
                {/* canWrite 는 서버가 계산해 내려준다(공지사항 게시판은 관리자만 true). */}
                {boardType.canWrite && (
                    <ActionButton variant="add" onClick={() => onCompose()} sx={{ fontSize: 12.5 }} label="글쓰기" />
                )}
            </Stack>

            <Paper elevation={0} sx={{
                border: `1px solid ${C.borderLight}`, borderRadius: 1.5, overflow: "hidden", bgcolor: C.surface,
            }}>
                {/* 표 헤더 — 목록의 유일한 가로선(헤더/본문 경계)만 남긴다. */}
                <Stack direction="row" alignItems="center" spacing={1}
                    sx={{ bgcolor: BOARD_HEADER_BG, px: 2, py: 1.1 }}>
                    <Typography sx={{ flex: 1, fontSize: 12.5, fontWeight: 700, color: "#fff" }}>제목</Typography>
                    <Typography sx={{ width: 90, fontSize: 12.5, fontWeight: 700, color: "#fff", textAlign: "center" }}>작성자</Typography>
                    <Typography sx={{ width: 70, fontSize: 12.5, fontWeight: 700, color: "#fff", textAlign: "center" }}>작성일</Typography>
                    <Typography sx={{ width: 52, fontSize: 12.5, fontWeight: 700, color: "#fff", textAlign: "center" }}>조회</Typography>
                </Stack>

                {loading ? (
                    <LoadingDots />
                ) : rows.length === 0 ? (
                    <Box sx={{ py: 7, textAlign: "center" }}>
                        <Typography sx={{ fontSize: 13.5, color: C.textSecondary, fontWeight: 600 }}>
                            {keyword ? "검색 결과가 없습니다" : "등록된 글이 없습니다"}
                        </Typography>
                        {!keyword && boardType.canWrite && (
                            <Typography sx={{ fontSize: 12.5, color: C.textMuted, mt: 0.5 }}>
                                '글쓰기' 로 첫 글을 남겨 보세요.
                            </Typography>
                        )}
                    </Box>
                ) : rows.map(row => (
                    <Stack key={row.id} direction="row" alignItems="center" spacing={1}
                        onClick={() => onOpenPost(row.id)}
                        sx={{
                            px: 2, py: 1.2,
                            ...ROW_HOVER_SX,
                            ...(row.notice ? { bgcolor: NOTICE_ROW_BG, "&:hover": { bgcolor: C.primaryLight } } : {}),
                        }}>
                        <Stack direction="row" alignItems="center" spacing={0.6} sx={{ flex: 1, minWidth: 0 }}>
                            {row.notice && (
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.primary, flexShrink: 0 }}>
                                    [공지]
                                </Typography>
                            )}
                            <Typography noWrap sx={{
                                fontSize: 13.5, minWidth: 0,
                                fontWeight: row.notice ? 700 : 500,
                                color: row.notice ? C.textPrimary : "#334155",
                            }}>
                                {/* 답글은 목록에서 '└' 로 원글에 딸린 글임을 표시(들여쓰기 트리 대신 1단 표기) */}
                                {row.reply ? "└ " : ""}{row.title}
                            </Typography>
                            {row.attachmentCount > 0 && (
                                <AttachFileRoundedIcon sx={{ fontSize: 14, color: C.textMuted, flexShrink: 0 }} />
                            )}
                            {row.commentCount > 0 && (
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#ea580c", flexShrink: 0 }}>
                                    [{row.commentCount}]
                                </Typography>
                            )}
                        </Stack>
                        <Typography noWrap sx={{ width: 90, fontSize: 12.5, color: C.textSecondary, textAlign: "center" }}>
                            {row.authorName || "—"}
                        </Typography>
                        <Typography sx={{ width: 70, fontSize: 12.5, color: C.textMuted, textAlign: "center" }}>
                            {formatBoardDate(row.createdAt)}
                        </Typography>
                        <Typography sx={{ width: 52, fontSize: 12.5, color: C.textMuted, textAlign: "center" }}>
                            {row.viewCount ?? 0}
                        </Typography>
                    </Stack>
                ))}
            </Paper>

            {totalPages > 1 && (
                <Stack alignItems="center" mt={2}>
                    <Pagination
                        count={totalPages}
                        page={page + 1}
                        onChange={(_, p) => setPage(p - 1)}
                        size="small" shape="rounded" color="primary"
                    />
                </Stack>
            )}
        </Box>
    );
}
